import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MessageSquare, ChevronLeft, ChevronRight, CheckCircle, AlertCircle, Loader2, Upload, X, FileText, Image as ImageIcon, Download } from 'lucide-react';
import { toast } from 'react-toastify';
import Backdrop from 'components/Backdrop';
import {
  startGeneralQuestionnaire,
  getGeneralQuestionnaireCurrentState,
  getGeneralQuestionnaireQuestions,
  submitGeneralQuestionResponse,
  completeGeneralQuestionnaire
} from 'api/apiHelper';

interface Question {
  id: number;
  question_text: string;
  question_order: number;
}

interface Response {
  question_id: number;
  question_order: number;
  answer_text: string;
}

interface QuestionnaireSession {
  id: number;
  test_token: string;
  status: string;
  started_at: string | null;
  responses: Array<{
    question_order: number;
    answer_text: string;
  }>;
}

interface GeneralQuestionnaireScreenProps {
  onComplete?: () => void;
  testToken?: string;
}

const GeneralQuestionnaireScreen: React.FC<GeneralQuestionnaireScreenProps> = ({ onComplete, testToken: propTestToken }) => {
  const { testToken: paramTestToken } = useParams<{ testToken: string }>();
  const testToken = propTestToken || paramTestToken;
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [attachments, setAttachments] = useState<Record<number, File | null>>({});
  const [attachmentPreviews, setAttachmentPreviews] = useState<Record<number, string | null>>({});
  const [existingAttachments, setExistingAttachments] = useState<Record<number, string>>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showConfirmSubmit, setShowConfirmSubmit] = useState<boolean>(false);
  const [submittingAnswer, setSubmittingAnswer] = useState<boolean>(false);
  const [session, setSession] = useState<QuestionnaireSession | null>(null);
  const saveTimeoutRef = useRef<Record<number, NodeJS.Timeout>>({});

  useEffect(() => {
    if (testToken) {
      initializeQuestionnaire();
    }
  }, [testToken]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(saveTimeoutRef.current).forEach(timeout => {
        if (timeout) clearTimeout(timeout);
      });
    };
  }, []);

  const initializeQuestionnaire = async () => {
    setLoading(true);
    setError('');
    
    try {
      const stateResponse = await getGeneralQuestionnaireCurrentState(testToken!);
      console.log('Current questionnaire state:', stateResponse.data);
      
      if (stateResponse.data.status === 'pending') {
        const startResponse = await startGeneralQuestionnaire(testToken!);
        console.log('Questionnaire started:', startResponse.data);
        setSession(startResponse.data);
      } else {
        setSession(stateResponse.data);
      }

      const questionsResponse = await getGeneralQuestionnaireQuestions(testToken!);
      console.log('Questions:', questionsResponse.data);
      setQuestions(questionsResponse.data.questions || []);

      // Load existing responses
      const initialAnswers: Record<number, string> = {};
      const initialAttachments: Record<number, string> = {};
      if (stateResponse.data.responses) {
        stateResponse.data.responses.forEach((response: any) => {
          const question = questionsResponse.data.questions.find((q: Question) => q.question_order === response.question_order);
          if (question) {
            initialAnswers[question.id] = response.answer_text || '';
            if (response.attachment_url) {
              initialAttachments[question.id] = response.attachment_url;
            }
          }
        });
      }
      setAnswers(initialAnswers);
      setExistingAttachments(initialAttachments);
      
      // Also check responses from session data
      if (session?.responses) {
        session.responses.forEach((response: any) => {
          const question = questionsResponse.data.questions.find((q: Question) => q.question_order === response.question_order);
          if (question && response.attachment_url && !initialAttachments[question.id]) {
            initialAttachments[question.id] = response.attachment_url;
          }
        });
        setExistingAttachments(initialAttachments);
      }
    } catch (error: any) {
      console.error('Questionnaire initialization error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to initialize questionnaire';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId: number, value: string) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const saveAnswer = useCallback(async (questionId: number, answerText: string, attachment?: File | null) => {
    if (!answerText.trim() && !attachment) return;

    try {
      setSubmittingAnswer(true);
      const response = await submitGeneralQuestionResponse(testToken!, {
        question_id: questionId,
        answer_text: answerText || '',
        attachment: attachment || undefined
      });
      // Answer saved successfully
      // If attachment was saved, use the server URL from response
      if (attachment && response.data?.response?.attachment_url) {
        // Move attachment to existingAttachments using server URL
        setExistingAttachments(prev => ({
          ...prev,
          [questionId]: response.data.response.attachment_url
        }));
        // Clear from pending attachments since it's now saved
        setAttachments(prev => {
          const newAttachments = { ...prev };
          delete newAttachments[questionId];
          return newAttachments;
        });
        setAttachmentPreviews(prev => {
          const newPreviews = { ...prev };
          delete newPreviews[questionId];
          return newPreviews;
        });
      }
    } catch (error: any) {
      console.error('Error saving answer:', error);
      toast.error('Failed to save answer. Please try again.');
    } finally {
      setSubmittingAnswer(false);
    }
  }, [testToken]);

  const handleFileChange = (questionId: number, file: File | null) => {
    if (!file) {
      setAttachments(prev => {
        const newAttachments = { ...prev };
        delete newAttachments[questionId];
        return newAttachments;
      });
      setAttachmentPreviews(prev => {
        const newPreviews = { ...prev };
        delete newPreviews[questionId];
        return newPreviews;
      });
      return;
    }

    // Validate file size (10MB max)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File size cannot exceed 10MB');
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload an image (JPEG, PNG, GIF, WebP) or PDF');
      return;
    }

    setAttachments(prev => ({
      ...prev,
      [questionId]: file
    }));

    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setAttachmentPreviews(prev => ({
          ...prev,
          [questionId]: reader.result as string
        }));
      };
      reader.readAsDataURL(file);
    } else {
      setAttachmentPreviews(prev => ({
        ...prev,
        [questionId]: null
      }));
    }
  };

  const removeAttachment = (questionId: number) => {
    setAttachments(prev => {
      const newAttachments = { ...prev };
      delete newAttachments[questionId];
      return newAttachments;
    });
    setAttachmentPreviews(prev => {
      const newPreviews = { ...prev };
      delete newPreviews[questionId];
      return newPreviews;
    });
  };

  const handleNext = () => {
    const currentQuestion = questions[currentQuestionIndex];
    if (currentQuestion) {
      const answerText = answers[currentQuestion.id] || '';
      const attachment = attachments[currentQuestion.id] || null;
      if (answerText.trim() || attachment) {
        saveAnswer(currentQuestion.id, answerText, attachment);
      }
    }
    
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // Save current answer if any
      const currentQuestion = questions[currentQuestionIndex];
      if (currentQuestion) {
        const answerText = answers[currentQuestion.id] || '';
        const attachment = attachments[currentQuestion.id] || null;
        if (answerText.trim() || attachment) {
          await saveAnswer(currentQuestion.id, answerText, attachment);
        }
      }

      // Save all remaining answers
      for (const question of questions) {
        const answerText = answers[question.id] || '';
        const attachment = attachments[question.id] || null;
        if (answerText.trim() || attachment) {
          await submitGeneralQuestionResponse(testToken!, {
            question_id: question.id,
            answer_text: answerText,
            attachment: attachment || undefined
          });
        }
      }

      // Complete the questionnaire
      await completeGeneralQuestionnaire(testToken!);
      toast.success('Questionnaire submitted successfully!');
      // If onComplete callback provided, use it (unified flow), otherwise navigate
      if (onComplete) {
        onComplete();
      } else {
        navigate(`/general-questionnaire/thank-you/${testToken}`);
      }
    } catch (error: any) {
      console.error('Error submitting questionnaire:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to submit questionnaire';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
      setShowConfirmSubmit(false);
    }
  };

  const getProgress = useCallback(() => {
    const answeredCount = questions.filter(q => {
      const hasAnswer = answers[q.id] && answers[q.id].trim();
      const hasAttachment = attachments[q.id] || existingAttachments[q.id];
      return hasAnswer || hasAttachment;
    }).length;
    return questions.length > 0 ? Math.round((answeredCount / questions.length) * 100) : 0;
  }, [questions, answers, attachments, existingAttachments]);

  if (loading) {
    return <Backdrop message="Loading questionnaire..." />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
          <div className="flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-2">Error</h2>
          <p className="text-center text-gray-600 mb-6">{error}</p>
        </div>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">No Questions Available</h2>
          <p className="text-gray-600">There are no questions configured for this questionnaire.</p>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const currentAnswer = currentQuestion ? answers[currentQuestion.id] || '' : '';
  const currentAttachment = currentQuestion ? attachments[currentQuestion.id] || null : null;
  const currentAttachmentPreview = currentQuestion ? attachmentPreviews[currentQuestion.id] || null : null;
  const currentExistingAttachment = currentQuestion ? existingAttachments[currentQuestion.id] || null : null;
  const progress = getProgress();
  const isLastQuestion = currentQuestionIndex === questions.length - 1;
  const allAnswered = questions.every(q => {
    const hasAnswer = answers[q.id] && answers[q.id].trim();
    const hasAttachment = attachments[q.id] || existingAttachments[q.id];
    return hasAnswer || hasAttachment;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <MessageSquare className="w-8 h-8 text-brand-500 mr-3" />
              <div>
                <h1 className="text-2xl font-bold text-gray-800">General Questionnaire</h1>
                <p className="text-sm text-gray-600">Question {currentQuestionIndex + 1} of {questions.length}</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-600 mb-1">Progress</div>
              <div className="text-2xl font-bold text-brand-500">{progress}%</div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4">
            <div
              className="bg-brand-500 h-2.5 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>

          <div className="text-sm text-gray-600">
            {questions.filter(q => {
              const hasAnswer = answers[q.id] && answers[q.id].trim();
              const hasAttachment = attachments[q.id] || existingAttachments[q.id];
              return hasAnswer || hasAttachment;
            }).length} of {questions.length} questions answered
          </div>
        </div>

        {/* Question Card */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
          <div className="mb-6">
            <div className="flex items-center mb-4">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-brand-100 text-brand-700 font-bold text-lg mr-4">
                {currentQuestionIndex + 1}
              </span>
              <h2 className="text-xl font-semibold text-gray-800">Question {currentQuestionIndex + 1}</h2>
            </div>
            <p className="text-lg text-gray-700 leading-relaxed">{currentQuestion.question_text}</p>
          </div>

          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Your Answer {!currentAttachment && !currentExistingAttachment && !currentAnswer.trim() && <span className="text-red-500">*</span>}
            </label>
            <div className="relative">
              <textarea
                value={currentAnswer}
                onChange={(e) => {
                  const newValue = e.target.value;
                  handleAnswerChange(currentQuestion.id, newValue);
                  
                  // Clear existing timeout for this question
                  if (saveTimeoutRef.current[currentQuestion.id]) {
                    clearTimeout(saveTimeoutRef.current[currentQuestion.id]);
                  }
                  
                  // Auto-save after a delay (only if there's content or attachment)
                  saveTimeoutRef.current[currentQuestion.id] = setTimeout(() => {
                    // Get current state values at the time of save
                    const currentAttachmentValue = attachments[currentQuestion.id] || null;
                    if (newValue.trim() || currentAttachmentValue) {
                      saveAnswer(currentQuestion.id, newValue, currentAttachmentValue);
                    }
                  }, 1000);
                }}
                rows={8}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent resize-none"
                placeholder="Type your answer here..."
                style={{ minHeight: '200px' }}
              />
              <div className="absolute bottom-2 right-2 h-6">
                {submittingAnswer && (
                  <div className="text-xs text-gray-500 flex items-center bg-white/80 px-2 py-1 rounded">
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    Saving...
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Attachment Section */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Attachment (Optional)
            </label>
            <p className="text-xs text-gray-500 mb-3">
              You can attach an image (JPEG, PNG, GIF, WebP) or PDF file (max 10MB)
            </p>

            {/* Existing Attachment */}
            {currentExistingAttachment && !currentAttachment && (
              <div className="mb-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <FileText className="w-5 h-5 text-brand-500 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-700">Current Attachment</p>
                      <a
                        href={currentExistingAttachment}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-brand-500 hover:text-brand-600 flex items-center mt-1"
                      >
                        <Download className="w-3 h-3 mr-1" />
                        View/Download
                      </a>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      // Remove existing attachment by uploading a new one or clearing
                      setExistingAttachments(prev => {
                        const newAttachments = { ...prev };
                        delete newAttachments[currentQuestion.id];
                        return newAttachments;
                      });
                    }}
                    className="text-red-500 hover:text-red-600"
                    title="Remove attachment"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* New Attachment Preview */}
            {currentAttachment && (
              <div className="mb-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    {currentAttachmentPreview ? (
                      <>
                        <img
                          src={currentAttachmentPreview}
                          alt="Preview"
                          className="w-16 h-16 object-cover rounded mr-3"
                        />
                        <div>
                          <p className="text-sm font-medium text-gray-700">{currentAttachment.name}</p>
                          <p className="text-xs text-gray-500">
                            {(currentAttachment.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </>
                    ) : (
                      <>
                        <FileText className="w-8 h-8 text-brand-500 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-gray-700">{currentAttachment.name}</p>
                          <p className="text-xs text-gray-500">
                            {(currentAttachment.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                  <button
                    onClick={() => removeAttachment(currentQuestion.id)}
                    className="text-red-500 hover:text-red-600"
                    title="Remove attachment"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}

            {/* File Upload Input */}
            {!currentAttachment && (
              <label className="flex items-center justify-center w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-brand-500 hover:bg-gray-50 transition-colors">
                <Upload className="w-5 h-5 text-gray-400 mr-2" />
                <span className="text-sm text-gray-600">
                  {currentExistingAttachment ? 'Replace Attachment' : 'Choose file to attach (optional)'}
                </span>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    handleFileChange(currentQuestion.id, file);
                    // Auto-save after file selection
                    if (file) {
                      // Clear any pending text save timeout
                      if (saveTimeoutRef.current[currentQuestion.id]) {
                        clearTimeout(saveTimeoutRef.current[currentQuestion.id]);
                      }
                      // Save with current answer text and new file
                      setTimeout(() => {
                        const currentAnswerValue = answers[currentQuestion.id] || '';
                        saveAnswer(currentQuestion.id, currentAnswerValue, file);
                      }, 500);
                    }
                    // Reset file input to allow re-selecting same file
                    e.target.value = '';
                  }}
                  className="hidden"
                />
              </label>
            )}
          </div>
        </div>

        {/* Navigation */}
        <div className="bg-white rounded-lg shadow-lg p-6 flex justify-between items-center">
          <button
            onClick={handlePrevious}
            disabled={currentQuestionIndex === 0}
            className={`flex items-center px-6 py-3 rounded-lg font-medium transition-all ${
              currentQuestionIndex === 0
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <ChevronLeft className="w-5 h-5 mr-2" />
            Previous
          </button>

          <div className="flex gap-3">
            {isLastQuestion ? (
              <button
                onClick={() => setShowConfirmSubmit(true)}
                disabled={!allAnswered || isSubmitting}
                className={`flex items-center px-8 py-3 rounded-lg font-semibold text-white transition-all ${
                  !allAnswered || isSubmitting
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                <CheckCircle className="w-5 h-5 mr-2" />
                Submit Questionnaire
              </button>
            ) : (
              <button
                onClick={handleNext}
                className="flex items-center px-6 py-3 bg-brand-500 hover:bg-brand-600 text-white rounded-lg font-medium transition-all"
              >
                Next
                <ChevronRight className="w-5 h-5 ml-2" />
              </button>
            )}
          </div>
        </div>

        {/* Confirm Submit Modal */}
        {showConfirmSubmit && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Confirm Submission</h2>
              <p className="text-gray-600 mb-6">
                Are you sure you want to submit your questionnaire? You won't be able to make changes after submission.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowConfirmSubmit(false)}
                  disabled={isSubmitting}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-all disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-all disabled:opacity-50 flex items-center"
                >
                  {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {isSubmitting ? 'Submitting...' : 'Confirm Submit'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GeneralQuestionnaireScreen;
